/**
 * What the campaign card says, where a runner can reach it — for the reason
 * `overview-copy.ts` gives. `components/CampaignCard.tsx` renders these and decides
 * nothing about them; the decisions are in `campaign-card.ts`.
 *
 * 🔴 **Most of the card's sentences are borrowed, not written here**, because each
 * already has a test and a mutation table on the screen it came from, and a second
 * wording of the same fact is a second place to be wrong: the Served answer is
 * `servableText` (campaign-copy.ts), the budget is `budgetSummary` through money.ts
 * (create-ad-copy.ts), the Ads Manager number is `adsManagerText` with its provenance
 * (create-ad-copy.ts), the objective's name is `objectiveSummary` (create-ad-copy.ts),
 * a bid is `bidText` and a flight in X's time is `flightText` (line-item-copy.ts), the
 * «stop first» hint is `creativeHint` (line-item-copy.ts), X's review of a creative is
 * `approvalNote` and a promoted tweet's row is `promotedLabel` (creative-copy.ts), a
 * criterion is `criterionLabel` and an empty criteria list is `NO_CRITERIA`
 * (targeting-copy.ts), a figure's five states are `billedCellText` and the notes under
 * them are `SPEND_NOTES` and `FIGURES_NOTE` (stats-copy.ts). What is written here is only what no screen said before: the
 * card's own frame, the two times in the reader's clock, where a country's name came
 * from or did not, why a tweet's text is missing, and what this console does not know
 * about who made the campaign.
 *
 * 🔴 Three sentences here are bounded to what this console has seen, and
 * `campaign-card-copy.test.ts` holds the boundary: the origin line names what is NOT
 * known rather than guessing an author; a country's id without a name says which list
 * was read and whether it was whole; a missing tweet text says which of four causes it
 * has. Nothing here says «delivered», «live» or «running», and nothing is a superlative
 * about this console's history.
 */
import type { LineItem, Tweet } from "./api.ts";
import { MAX_PAGES, TWEET_IDS_MAX, type TweetText } from "./campaign-card.ts";
import { servableText } from "./campaign-copy.ts";
import { objectiveSummary } from "./create-ad-copy.ts";
import { tweetKind, tweetLabel } from "./creative-copy.ts";
import { OBJECTIVES, flightText } from "./line-item-copy.ts";
import { SPEND_LABEL, billedCellText } from "./stats-copy.ts";
import type { BilledRowState, StatsMetric } from "./stats.ts";

/** The card's heading line, under the campaign's name. */
export const CARD_INTRO =
	"Everything this console reads about this campaign, on one card: the campaign as X holds it, each line item (X's ad group) under it, what narrows each one, and what each one promotes — with the tweet's text. Since 2026-10-21 the card is also where a campaign is managed: Edit the campaign and each line item, Remove a place, Add ad under a PAUSED line item, Delete a line item or the campaign.";

/** The button on the campaign's row, both ways. */
export const OPEN_CARD = "Card";
export const CLOSE_CARD = "Hide card";

/** The card's way to the switches and the Ads panel: the same panel the row's Line items button opens. */
export const OPEN_LINE_ITEMS = "Open Line items for Start, Pause, Promote and Add a place";

/**
 * The card's three panels (the GUI integration round of audits/2026-10-29/gui/plan.md): the
 * campaign, its line items, and ONE line item's edit, targeting and ads — the one whose
 * row's `SHOW_LINE_ITEM` was pressed. Until then the card stacked a block per line item,
 * three tables each, under the list (gui-spec §2).
 */
export const CARD_CAMPAIGN_PANEL = "Campaign";
export const CARD_LINE_ITEMS_PANEL = "Line items";
export const CARD_LINE_ITEM_PANEL = "Line item";

/** On a line item's row in the card's table, both ways. */
export const SHOW_LINE_ITEM = "Show";
export const HIDE_LINE_ITEM = "Hide";

/** The third panel while no row is shown. */
export const CARD_LINE_ITEM_EMPTY = `Press ${SHOW_LINE_ITEM} on a line item's row in the table to see its edit, its targeting and its ads here.`;

// ── Times, in the reader's clock and in X's ───────────────────────────

/**
 * A time X sent, in the reader's clock for the cell and as X wrote it for the title.
 * `format` is injectable so a case can pin the shape without pinning a locale, as
 * `syncedLabel` in format.ts does. A string that is not a time is shown as X sent it,
 * never as «Invalid Date».
 */
export function whenText(
	iso: string | null,
	format: (iso: string) => string = (s) => new Date(s).toLocaleString(),
): { local: string; utc: string } {
	if (iso === null) return { local: "no time", utc: "X sent no time" };
	const when = new Date(iso);
	if (Number.isNaN(when.getTime())) return { local: iso, utc: iso };
	return { local: format(iso), utc: `${iso} as X sent it` };
}

/**
 * A line item's flight in the reader's clock, with X's own figures for the title —
 * the title is `flightText`, so an absent end still says what an absent end means.
 */
export function flightLocal(
	item: Pick<LineItem, "startTime" | "endTime">,
	format?: (iso: string) => string,
): { local: string; utc: string } {
	const start = item.startTime === null ? "no start" : whenText(item.startTime, format).local;
	const end =
		item.endTime === null
			? "no end — it serves until stopped"
			: whenText(item.endTime, format).local;
	return { local: `${start} → ${end}`, utc: flightText(item) };
}

// ── The campaign ──────────────────────────────────────────────────────

/**
 * 🔴 What this console knows about where the campaign came from, and what it does not.
 * It knows the time X holds as `created_at`. It does not know who made it — this
 * console, an agent through the API, or someone in Ads Manager: the worker records the
 * console's own writes to X in access_audit (lib/x-write-trail.ts), but this card does
 * not read that trail, the trail holds nothing from before it began or from Ads
 * Manager, and X names no author. Said as a boundary, with no promise that the card
 * will name one.
 */
export function originText(createdLocal: string): string {
	return `Created in X on ${createdLocal}. Who created it — this console, an agent, or Ads Manager — is not something this card can say: it does not read the console's trail of its own writes to X, which holds nothing from before the trail began or from Ads Manager, and it reads no author from X.`;
}

/**
 * The empty line-items table. The console's own sentence says only that the list is
 * empty; whether X serves is X's answer, in the campaign's own words from
 * campaign-copy.ts, never a verdict of this sentence.
 */
export function noLineItemsText(servable: boolean, reasons: readonly string[]): string {
	return `No line items under this campaign: X lists none. ${servableText(servable, reasons)}`;
}

/** Under the line-items table: what is shown, and whether the reading stopped short. */
export function shownLabel(shown: number, moreNotRead: boolean): string {
	const rows = `${shown} ${shown === 1 ? "line item" : "line items"} shown`;
	return moreNotRead
		? `${rows}. More were not read: X's list goes past the ${MAX_PAGES} pages this card follows.`
		: `${rows}.`;
}

// ── A line item ───────────────────────────────────────────────────────

/**
 * The objective under the name the wizard shows for it, when it is one of the two v1
 * offers; X's own value otherwise, as it arrived. A line item made in Ads Manager can
 * carry any objective X knows, and this console names none it has not seen.
 */
export function objectiveLabel(objective: string): string {
	const offered = OBJECTIVES.find((o) => o.value === objective);
	return offered === undefined ? objective : objectiveSummary(offered.value);
}

/** The empty promoted-tweets table: an empty list, said as one. */
export const NO_AD = "No ad on this line item: X lists no promoted tweet under it.";

/**
 * Beside a criterion's id when the card could not name it. Four causes, told apart,
 * because «name not in list» over a list that was never read is a guess.
 */
export function countryNote(
	kind: "other-level" | "list-not-read" | "not-in-list" | "truncated",
): string {
	switch (kind) {
		case "other-level":
			return "name not looked up: not a country-level criterion, and this card reads X's country list only.";
		case "list-not-read":
			return "name not read: X's country list did not answer, so the id stands.";
		case "not-in-list":
			return "name not found: this id is not in X's country list as this card read it.";
		case "truncated":
			return "name not found in the part of X's country list this card read — the list was truncated, so the name may be past it.";
	}
}

/** The banner when the country list could not be read: the ids stay ids, said once. */
export const COUNTRIES_NOT_READ = {
	title: "X's country list could not be read.",
	body: "Each criterion below shows X's id and no name. The targeting itself is as X reports it; only the naming is missing.",
};

// ── A promoted tweet's text ───────────────────────────────────────────

/**
 * The text cell. A found tweet shows its text as X sent it — the text, not the name:
 * the owner's ad of 2026-10-21 is named «Ad 1» and reads «Built for retail, eCommerce
 * and entertainment brands across EMEA.», and the card is for the second. The four
 * ways there is no text each say which they are.
 */
export function tweetTextCell(state: TweetText): string {
	switch (state.kind) {
		case "found":
			return state.tweet.text ?? "(no text)";
		case "no-id":
			return "text not read: X sent this tweet's id as a number, so there was nothing to ask for.";
		case "not-asked":
			return `text not asked: this card reads up to ${TWEET_IDS_MAX} tweets in one request, and this one was past that.`;
		case "unread":
			return "text not read: the tweet read has not answered, or failed.";
		case "missing":
			return "text not read: X returned no PUBLISHED tweet for this id — a DRAFT or SCHEDULED tweet, or one X no longer lists, reads as nothing here.";
	}
}

/** The banner when the tweet read failed: every text cell says so, this names the error once. */
export const TWEETS_NOT_READ = "The tweets' text could not be read.";

/**
 * Beside the text: the tweet's name and whether it is promoted-only or organic. For a
 * named tweet this IS `tweetLabel` from creative-copy.ts, word for word (the test pins
 * it); for one without a name only the kind is said, because `tweetLabel` would put
 * the text there and the cell already shows it.
 */
export function tweetAside(t: Tweet): string | null {
	if (t.name !== null) return tweetLabel(t);
	const kind = tweetKind(t.nullcast);
	return kind === "" ? null : kind;
}

// ── The figures: spend, impressions, engagements, URL clicks over seven days ──

/**
 * The figures the card shows, in order — four of the five the stats route carries
 * (2026-10-21): X's `clicks` is read and not shown, because it counts «favorites and
 * other engagements» too and a column of it beside Engagements would be two names for
 * overlapping counts. It stays in `FIGURE_LABELS` so a screen that does show it names it
 * as X does.
 */
export const METRICS: readonly StatsMetric[] = [
	"billedChargeLocalMicro",
	"impressions",
	"engagements",
	"urlClicks",
];

/**
 * The column heads, with the window in them as the Spend column has it. 🔴 URL clicks is
 * named as X names the metric — `url_clicks` — and not «link clicks», until a live reading
 * has shown which of X's two click metrics Ads Manager's «link clicks» is (stats-copy.ts,
 * `FIGURES_NOTE`).
 */
export const FIGURE_LABELS: Record<StatsMetric, string> = {
	billedChargeLocalMicro: SPEND_LABEL,
	impressions: "Impressions 7d",
	engagements: "Engagements 7d",
	urlClicks: "URL clicks (url_clicks) 7d",
	clicks: "Clicks (clicks) 7d",
};

/**
 * One cell of a figure. Spend is `billedCellText` word for word — money through
 * money.ts, and the five states of the Spend column. A count is the same five states
 * with one difference: a value is the number X sent, and never money.
 *
 * 🔴 Three different sentences for three different facts, as on the Spend column: a
 * dash is X holding nothing for the window (X returns `null` there); «0» or «0.00 EUR»
 * is a figure X reported; «reading…» and «read failed» are this console not having an
 * answer. Collapsing any two would report a spend of nothing for an answer nobody read.
 */
export function figureCellText(
	state: BilledRowState,
	metric: StatsMetric,
	currency: string | null,
): string {
	if (
		metric !== "billedChargeLocalMicro" &&
		state.kind === "figure" &&
		state.figure.kind === "value"
	)
		return String(state.figure.micro);
	return billedCellText(state, currency);
}

/*
 * The sentence under the figures — which four, from where, what X's two click metrics
 * are and which one is shown — is `FIGURES_NOTE` in stats-copy.ts, the one module allowed
 * the word «spend», with `figuresReadText` beside it. Nothing here says that word.
 */
